// src/components/Dashboard/Sidebar.js
import React, { useState, Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    FiChevronLeft,
    FiChevronRight,
    FiHome,
    FiBookmark,
    FiEdit3,
    FiUser,
    FiGrid,
    FiLogOut,
    FiVideo,
    FiFilm,
    FiPlayCircle,
} from 'react-icons/fi';

const Sidebar = ({ userProfile, isCollapsed, setIsCollapsed }) => {
    
    const location = useLocation();

    const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });

    const navigate = useNavigate();

    const showTooltip = (e, text) => {
        const rect = e.currentTarget.getBoundingClientRect();

        setTooltip({
            show: true,
            text,
            x: rect.right + 10,
            y: rect.top + rect.height / 2,
        });
    };

    const hideTooltip = () => {
        setTooltip({ show: false, text: '', x: 0, y: 0 });
    };

    const handleLogout = () => {
        navigate('/logout');
    };

    const toggleSidebar = () => {
        setIsCollapsed(prev => !prev);
    };

    const menuItems = [
        { label: 'Dashboard', icon: <FiGrid />, path: '/dashboard' },
        { label: 'Clips', icon: <FiVideo />, path: '/clips' },
        { label: 'Specials', icon: <FiFilm />, path: '/comedians' },
        { label: 'Comedies', icon: <FiPlayCircle />, path: '/comedies' },
        { label: 'Saved Articles', icon: <FiBookmark />, path: '/bookmarks' },
        { label: 'Stories', icon: <FiEdit3 />, path: '/stories' },
        { label: 'Profile', icon: <FiUser />, path: '/profile' },
        { label: 'Home Page', icon: <FiHome />, path: '/' },
    ];

    return (
        <nav
            className="sidebar bg-light d-flex flex-column"
            style={{
                width: isCollapsed ? '60px' : '220px',
                minWidth: isCollapsed ? '60px' : '220px', // ✅ key fix
                flexShrink: 0, // ✅ prevents overlap
                height: '100vh', // optional but recommended
                position: 'fixed',
                transition: 'width 0.3s',
                boxShadow: '2px 0 15px rgba(0,0,0,0.05)',
            }}
        >
            {/* Toggle Button */}
            <div className="text-center mt-2">
                <button
                    onClick={toggleSidebar}
                    className="btn btn-sm mb-3"
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: '#fff',
                        border: '1px solid #eee',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        color: '#111827', // 👈 important (icon color)
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.background = '#f3f4f6';
                    }}

                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.background = '#fff';
                    }}
                >
                    {isCollapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
                </button>
            </div>

            {!isCollapsed && <h2 className="sidebar-heading text-center fw-bold mt-1">Menu</h2>}

            {/* Menu Items */}
            <ul className="nav flex-column mt-2" >
                {menuItems.map((item) => {
                    const isActive = location.pathname === item.path;

                    return (
                        <Fragment key={item.path}>
                            <li className="nav-item">
                                <Link
                                    to={item.path}
                                    className="nav-link text-dark d-flex align-items-center fw-bold"
                                    style={{
                                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                                        paddingLeft: isCollapsed ? '0' : '1rem',
                                        paddingRight: isCollapsed ? '0' : '1rem',

                                        // ⭐ PILL ACTIVE STYLE
                                        backgroundColor: isActive ? '#e9eef7' : 'transparent',
                                        borderLeft: isActive ? '3px solid #c7d2fe' : '3px solid transparent',
                                        borderRadius: '5px',
                                        fontWeight: isActive ? '700' : '500',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = '#e5e7eb';
                                        }
                                    }}

                                    onMouseLeave={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <span
                                        className="d-flex justify-content-center align-items-center"
                                        onMouseEnter={(e) => showTooltip(e, item.label)}
                                        onMouseLeave={hideTooltip}
                                    >
                                        {item.icon}
                                    </span>
                                    {!isCollapsed && <span className="ms-2">{item.label}</span>}
                                </Link>
                            </li>
                            <hr
                                className="divider"
                                style={{
                                    margin: isCollapsed ? '0 8px' : '0.5rem 1rem',
                                    borderColor: '#000',
                                }}
                            />
                        </Fragment>
                    );
                })}
            </ul>

            {/* User Info and Logout */}
            <div className="sidebar-user-info mt-auto mb-3 text-center fw-bold">
                {!isCollapsed && userProfile && <p>Welcome, {userProfile.username}!</p>}
                <button
                    onClick={handleLogout}
                    className="btn btn-sm btn-danger fw-bold d-flex align-items-center justify-content-center mx-auto"
                    style={{
                        width: isCollapsed ? '40px' : '100%',
                        height: '40px',
                        padding: 0,
                    }}
                >
                    {isCollapsed ? <FiLogOut size={20} /> : 'Logout'}
                </button>
            </div>
            {tooltip.show && (
                <div
                    style={{
                        position: 'fixed',
                        top: tooltip.y,
                        left: tooltip.x,
                        transform: 'translateY(-50%)',
                        backgroundColor: '#111',
                        color: '#fff',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        zIndex: 9999,
                        pointerEvents: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                >
                    {tooltip.text}
                </div>
            )}
        </nav>
    );
};

export default Sidebar;
